// Shared order preview dialog for the unified ReconWorkspace. Opened by clicking an order id (or the
// preview icon) on any PSP recon page. Shows the ledger order read-only with an "Edit order" action
// (view-first, never jump straight to the edit screen — see docs/ui-ux-guidelines.md) and, for a
// needs_link row, a "Link this order" action. PSP specifics come from `peek` config.
//
// peek = {
//   link: (ctx) => Promise,          // POST that stamps the PSP id onto the order
//   idMetaKeys: ['authnet_trans_id'],// order.meta_data key(s) that hold the PSP id when linked
//   idLabel: 'Authorize.Net id on order',
// }
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Divider, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { get } from '../../utils/api';
import { fmtMoney, fmtDate } from './reconShared';

const statusColor = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('complet')) return 'success';
  if (v.includes('cancel') || v.includes('refund') || v.includes('fail')) return 'error';
  if (v.includes('hold') || v.includes('pending')) return 'warning';
  return 'default';
};

export default function OrderPeekDialog({ ctx, peek, onClose, onLinked, navigate }) {
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!ctx) return undefined;
    let cancel = false;
    (async () => {
      setLoading(true); setErr(''); setOrder(null); setItems([]);
      try {
        const res = await get(`/ldb/orders/${ctx.orderId}`);
        if (cancel) return;
        setOrder(res?.order || null);
        setItems(Array.isArray(res?.items) ? res.items : []);
      } catch (e) { if (!cancel) setErr(e?.message || 'Failed to load order'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [ctx]);

  const idKeys = peek?.idMetaKeys || [];
  const idOnOrder = order?.meta_data ? idKeys.map((k) => order.meta_data[k]).find(Boolean) || null : null;

  const doLink = async () => {
    setLinking(true);
    try {
      await peek.link(ctx);
      onLinked(ctx);
    } catch (e) { setErr(e?.message || 'Link failed'); setLinking(false); }
  };

  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Order {ctx?.orderNumber || ctx?.orderId}</Typography>
          {order && <Chip size="small" color={statusColor(order.status)} label={order.status} />}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        {loading ? <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={26} /></Box>
          : err ? <Alert severity="error">{err}</Alert>
            : order ? (
              <Box>
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Donor</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{`${order.billing_first_name || ''} ${order.billing_last_name || ''}`.trim() || '—'}</Typography>
                      {order.billing_email && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{order.billing_email}</Typography>}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtMoney(order.total_num)}</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Date</Typography><Typography variant="body2">{fmtDate(order.date_created_gmt)}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Payment</Typography><Typography variant="body2">{order.payment_method_title || '—'}</Typography></Grid>
                    {peek?.idLabel && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">{peek.idLabel}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: idOnOrder ? 'monospace' : 'inherit', fontSize: idOnOrder ? 12.5 : 14, wordBreak: 'break-all', color: idOnOrder ? 'text.primary' : 'text.secondary' }}>{idOnOrder || 'none — not linked yet'}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
                <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>Line items</Typography>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 0.5 }}>
                  <Table size="small">
                    <TableHead><TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}><TableCell>Item</TableCell><TableCell align="center">Qty</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                    <TableBody>
                      {items.map((it) => (<TableRow key={it.id} hover><TableCell>{it.name}</TableCell><TableCell align="center">{it.quantity}</TableCell><TableCell align="right">{fmtMoney(it.total_num)}</TableCell></TableRow>))}
                      {items.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>No line items</TableCell></TableRow>}
                      <TableRow><TableCell colSpan={2} align="right" sx={{ fontWeight: 700, borderBottom: 0 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 700, borderBottom: 0 }}>{fmtMoney(order.total_num)}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate(`/orders/${ctx.orderId}/edit`)}>Edit order</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        {ctx?.canLink && peek?.link && <Button variant="contained" color="success" onClick={doLink} disabled={linking}>{linking ? 'Linking…' : 'Link this order'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
